/**
 * Identifies tools that MUTATE data — the generic write entry point plus named
 * write ops caught by mutating-verb prefixes (create_/update_/delete_/set_/…).
 * NAME-based so gateway and frontend classify identically. Note this does NOT
 * include code_mode_exec; whether code-mode needs confirmation depends on the
 * agent mode (see agentToolRequiresConfirmation).
 */
export const AGENT_CONFIRM_REQUIRED_TOOLS = ['call_write_operation'];

const AGENT_CONFIRM_REQUIRED_PREFIXES = ['create_', 'update_', 'delete_', 'set_', 'revoke_', 'clear_', 'remove_'];

export function isAgentConfirmRequiredTool(toolName: string): boolean {
    return (
        AGENT_CONFIRM_REQUIRED_TOOLS.includes(toolName) ||
        AGENT_CONFIRM_REQUIRED_PREFIXES.some((prefix) => toolName.startsWith(prefix))
    );
}

/**
 * The agent's permission mode, chosen by the user in the panel.
 * - 'read_only' (default): reads run automatically; writes AND code_mode_exec
 *   require explicit per-action approval — nothing mutating or arbitrary-code
 *   runs without a click.
 * - 'writes_allowed': nothing requires approval — reads, writes, and code-mode
 *   all run automatically.
 */
export const AGENT_MODES = ['read_only', 'writes_allowed'] as const;
export type AgentMode = (typeof AGENT_MODES)[number];
export const DEFAULT_AGENT_MODE: AgentMode = 'read_only';

/**
 * Whether a tool needs explicit per-action user confirmation in the given mode.
 * Shared by the gateway (which tools get an auto-`execute`) and the frontend
 * (when to render the approve/reject card) so they always agree.
 */
export function agentToolRequiresConfirmation(toolName: string, mode: AgentMode): boolean {
    if (mode === 'writes_allowed') {
        return false;
    }
    // read_only: writes and code-mode (which can write) both need approval.
    return isAgentConfirmRequiredTool(toolName) || toolName === 'code_mode_exec';
}

/**
 * Key under which the frontend stores the user's write decision on a tool part's
 * output, and the gateway reads it back to execute or deny the pending write.
 * Shared so both sides agree on the marker shape.
 */
export const AGENT_WRITE_DECISION_KEY = '__agentWriteDecision';
export type AgentWriteDecision = 'approved' | 'rejected';
